<template>
    <div class="mt-10 rounded-md bg-white p-6 shadow-md">
        <div
            class="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between"
        >
            <div class="flex flex-col sm:flex-row sm:items-center">
                <div
                    class="h-20 w-20 shrink-0 cursor-pointer rounded-full ring-[1px] ring-gray-300"
                >
                    <img
                        :src="selectedUser.profile_photo_url"
                        class="h-20 w-20 rounded-full bg-white object-cover"
                        alt="User tab profile photo"
                    />
                </div>
                <div class="mt-2 flex flex-col sm:ml-10 sm:mt-0">
                    <h2 class="text-2xl font-bold text-gray-800">
                        {{ selectedUser.name }}
                    </h2>
                    <div class="text-xl font-medium text-gray-500">
                        {{ selectedUser.email }}
                    </div>
                </div>
            </div>

            <div
                class="flex flex-col flex-wrap gap-2 sm:flex-row sm:items-center sm:justify-between"
            >
                <div>
                    <AtomicButton
                        size="sm"
                        color="purple"
                        @click="handleImpersonate(selectedUser)"
                    >
                        <template #icon>
                            <EyeIcon class="mr-2 h-6 w-6" />
                        </template>
                        {{ t("vue.impersonate") }}
                    </AtomicButton>
                </div>

                <div>
                    <AtomicButton
                        size="sm"
                        color="yellow"
                        @click="handleResetPassword(selectedUser)"
                    >
                        <template #icon>
                            <AtSymbolIcon class="mr-2 h-6 w-6" />
                        </template>
                        {{ t("vue.reset_password") }}
                    </AtomicButton>
                </div>

                <div>
                    <AtomicButton
                        size="sm"
                        color="orange"
                        @click="handleRemoveFromEcomail(selectedUser)"
                    >
                        <template #icon>
                            <ExclamationTriangleIcon class="mr-2 h-6 w-6" />
                        </template>
                        Remove from Ecomail
                    </AtomicButton>
                </div>

                <div>
                    <AtomicButton
                        size="lg"
                        color="red"
                        @click="handleDeleteUser(selectedUser)"
                    >
                        <template #icon>
                            <TrashIcon class="mr-2 h-6 w-6" />
                        </template>
                        {{ t("vue.delete_user") }}
                    </AtomicButton>
                </div>
            </div>
        </div>

        <div class="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <div class="flex flex-col">
                <div class="flex">
                    <div class="text-sm font-medium text-gray-800">
                        {{ t("vue.id") }}
                    </div>

                    <div class="ml-1 text-sm text-gray-500">
                        {{ selectedUser.id }}
                    </div>
                </div>

                <div class="mt-1 flex">
                    <div class="text-sm font-medium text-gray-800">
                        {{ t("vue.joined") }}
                    </div>

                    <div class="ml-1 text-sm text-gray-500">
                        {{
                            format(
                                new Date(selectedUser.email_verified_at),
                                "d. L. yyyy '('HH:mm:ss')'",
                            )
                        }}
                    </div>
                </div>

                <div class="mt-1 flex">
                    <div class="text-sm font-medium text-gray-800">
                        {{ t("vue.last_login") }}
                    </div>

                    <div
                        v-if="selectedUser.last_login_at"
                        class="ml-1 text-sm text-gray-500"
                    >
                        {{
                            format(
                                new Date(selectedUser.last_login_at),
                                "d. L. yyyy '('HH:mm:ss')'",
                            )
                        }}
                    </div>
                </div>

                <div class="mt-1 flex items-center">
                    <div class="text-sm font-medium text-gray-800">
                        {{ t("vue.two_factor_authentication") }}
                    </div>

                    <div
                        v-if="selectedUser.two_factor_enabled"
                        class="ml-1 flex items-center text-sm font-medium text-green-600"
                    >
                        {{ t("vue.enabled") }}
                        <span
                            class="ml-1 cursor-pointer text-xs font-medium text-custom-red-600 underline hover:opacity-90"
                            @click="
                                handleDisableTwoFactorAuthentication(
                                    selectedUser,
                                )
                            "
                            >{{ t("vue.disable") }}</span
                        >
                    </div>
                    <div
                        v-else
                        class="ml-1 text-sm font-medium text-custom-red-600"
                    >
                        {{ t("vue.disabled") }}
                    </div>
                </div>

                <div class="flex">
                    <div class="text-sm font-medium text-gray-800">
                        {{ t("vue.domains_count") }}:
                    </div>

                    <div class="ml-1 text-sm text-gray-500">
                        {{ selectedUser.domains_quantity }}
                    </div>
                </div>

                <div class="flex">
                    <div class="text-sm font-medium text-gray-800">
                        {{ t("vue.subscription_type") }}:
                    </div>

                    <div class="ml-1 text-sm text-gray-500">
                        {{ selectedUser.subscription_type }}
                    </div>
                </div>

                <div class="flex">
                    <div class="text-sm font-medium text-gray-800">
                        {{ t("vue.subscription_period") }}:
                    </div>

                    <div class="ml-1 text-sm text-gray-500">
                        {{ selectedUser.subscription_period }}
                    </div>
                </div>

                <div class="flex">
                    <div class="text-sm font-medium text-gray-800">
                        {{ t("vue.subscription_amount") }}:
                    </div>

                    <div class="ml-1 text-sm text-gray-500">
                        {{ selectedUser.paddle_sub_amount_sum }}
                    </div>
                </div>

                <div class="mt-4">
                    <div class="text-sm font-medium text-gray-800">
                        {{ t("vue.note") }}
                    </div>
                    <div class="mt-1 max-w-xs text-base text-gray-500">
                        {{ selectedUser.admin_note }}
                    </div>
                    <div
                        class="mt-1 cursor-pointer text-xs font-medium text-blue-500 underline hover:opacity-90"
                        @click="editNote(selectedUser)"
                    >
                        {{ t("vue.add_edit_note") }}
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { format } from "date-fns";
import {
    TrashIcon,
    EyeIcon,
    AtSymbolIcon,
    ExclamationTriangleIcon,
} from "@heroicons/vue/24/outline";

import route from "ziggy-js";
import { router } from "@inertiajs/vue3";
import type IUser from "@/types/Members/IUser";
import useConfirm from "@/composables/useConfirm";
import { create } from "@/composables/useNotifications";
import axios from "axios";
import type ICurrentTeam from "@/types/Members/ICurrentTeam";
import { useTranslations } from "@/composables/useTranslations";

defineProps<{
    selectedUser: IUser;
    personalTeam: ICurrentTeam;
}>();

// Get access to translations
const { t } = useTranslations();

const { show } = useConfirm();

const handleDeleteUser = async (selectedUser: IUser) => {
    const confirmed = await show({
        header: `${t("vue.delete_user_header")} ${selectedUser.name}`,
        message: `${t("vue.delete_user_message")} ${selectedUser.name}?`,
        type: "danger",
        confirm: t("vue.delete_user_button"),
    });
    if (confirmed) {
        await deleteUser(selectedUser);
    }
};

const handleResetPassword = async (selectedUser: IUser) => {
    const confirmed = await show({
        header: `${t("vue.reset_password_header")} ${selectedUser.name}`,
        message: `${t("vue.reset_password_message")} ${selectedUser.name}?`,
        type: "danger",
        confirm: t("vue.reset_password_button"),
    });
    if (confirmed) {
        await resetPassword(selectedUser);
    }
};

const handleRemoveFromEcomail = async (selectedUser: IUser) => {
    const confirmed = await show({
        header: `Remove ${selectedUser.name} from Ecomail`,
        message: `Are you sure you want to remove ${selectedUser.name} from the Ecomail mailing list?`,
        type: "warning",
        confirm: "Remove from Ecomail",
    });
    if (confirmed) {
        await removeFromEcomail(selectedUser);
    }
};

const handleImpersonate = (selectedUser: IUser) => {
    router.get(route("kiosk.users.impersonate", { user: selectedUser.id }));
};

const deleteUser = async (selectedUser: IUser) => {
    try {
        const res = await axios.delete(
            route("kiosk.users.delete", { user: selectedUser.id }),
        );
        if (res.data.success) {
            create({
                title: t("vue.user_deleted"),
                type: "success",
            });
            router.get(route("kiosk.users.index"));
        } else {
            console.log(res.data);

            create({
                title: t("vue.user_not_deleted"),
                text: t("vue.error_deleting_user"),
                type: "error",
            });
        }
    } catch (err) {
        console.log(err);

        create({
            title: t("vue.user_not_deleted"),
            text: t("vue.error_deleting_user"),
            type: "error",
        });
    }
};

const resetPassword = async (selectedUser: IUser) => {
    try {
        const res = await axios.post(
            route("kiosk.users.reset-password", { user: selectedUser.id }),
        );
        if (res.data.success) {
            create({
                title: t("vue.reset_link_sent"),
                type: "success",
            });
        } else {
            create({
                title: t("vue.reset_link_not_sent"),
                text: t("vue.error_sending_reset_link"),
                type: "error",
            });
        }
    } catch (err) {
        create({
            title: t("vue.reset_link_not_sent"),
            text: t("vue.error_sending_reset_link"),
            type: "error",
        });
    }
};

const removeFromEcomail = async (selectedUser: IUser) => {
    try {
        const res = await axios.post(
            route("kiosk.users.remove-from-ecomail", { user: selectedUser.id }),
        );
        if (res.data.success) {
            create({
                title: "Ecomail Removal Queued",
                text: `${selectedUser.name} will be removed from Ecomail shortly.`,
                type: "success",
            });
        } else {
            create({
                title: "Failed to Queue Removal",
                text: "Error queueing user removal from Ecomail",
                type: "error",
            });
        }
    } catch (err) {
        create({
            title: "Failed to Queue Removal",
            text: "Error queueing user removal from Ecomail",
            type: "error",
        });
    }
};

const handleDisableTwoFactorAuthentication = async (selectedUser: IUser) => {
    const confirmed = await show({
        header: `${t("vue.disable_2fa_header")} ${selectedUser.name}`,
        message: `${t("vue.disable_2fa_message")} ${selectedUser.name}?`,
        type: "danger",
        confirm: t("vue.disable_button"),
    });
    if (confirmed) {
        await disableTwoFactorAuthentication(selectedUser);
    }
};

const disableTwoFactorAuthentication = (selectedUser: IUser) => {
    router.put(
        route("kiosk.users.2fa.disable", { user: selectedUser.id }),
        {},
        {
            preserveScroll: true,
            onSuccess: () => {
                create({
                    title: `${t("vue.2fa_disabled")} ${selectedUser.name} ${t(
                        "vue.disabled_success",
                    )}`,
                    type: "success",
                });
            },
            onError: () => {
                create({
                    title: `${t("vue.2fa_not_disabled")} ${
                        selectedUser.name
                    } ${t("vue.not_disabled")}`,
                    text: t("vue.error_disabling_2fa"),
                    type: "error",
                });
            },
        },
    );
};

const editNote = async (selectedUser: IUser) => {
    const note = await show({
        header: t("vue.add_edit_note_header"),
        hasInput: true,
        confirm: t("vue.update"),
        inputData: {
            label: t("vue.enter_note"),
        },
    });

    if (note) {
        router.put(
            route("kiosk.users.update", { user: selectedUser.id }),
            {
                note: note,
            },
            {
                preserveScroll: true,
            },
        );
    }
};
</script>
